import { Loader, LoadingOverlay } from '@mantine/core'
import { IconUserOff } from '@tabler/icons'
import { useRouter } from 'next/router'
import React from 'react'

import { Redirect, ViewDenied, ViewError } from '@/components/commons/'
import { useFetch } from '@/lib/hooks'
import { getSession, isEmailConfirmationRequired } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import {
  Channel as ChannelType,
  Product as ProductType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType,
  StudentSiteProfile as StudentSiteProfileType
} from '@/types'

import { ProtectedRequest, WelcomeAuth } from './components'

interface Data {
  site?: SiteType
  channel?: ChannelType
  store?: StoreType
  showcase?: ShowcaseType
  product?: ProductType
}
interface Props {
  initialData?: Data
  children?: React.ReactNode
}

export default function ChannelGuard({ initialData, children }: Props) {
  // Hooks
  const router = useRouter()
  const { isAuthenticated, permissionsData, isEmailConfirmed } = useAuth() || {}

  // Constants
  const indexPath = router?.pathname === '/_sites/[site]/[channel]'
  const {
    site: siteSlug,
    channel: channelSlug,
    store: storeSlug,
    showcase: showcaseSlug
  } = router.query || {}

  const channelProfile = permissionsData?.channels[`${channelSlug}`]
  const { isAuthorized, isActive } = channelProfile || {}

  const sessionStudentUid = getSession('stuid')

  // Fetch
  const { data: siteData, error: siteDataError } = useFetch(
    [siteSlug ? `/${siteSlug}/` : null],
    { fallbackData: initialData?.site } // rehydration if necessary
  )

  const { data: channelData, error: channelDataError } = useFetch(
    [siteSlug && channelSlug ? `/${siteSlug}/channels/${channelSlug}/` : null],
    { fallbackData: initialData?.channel } // rehydration if necessary
  )
  const isChannelPrivate = channelData?.privacy === 'private'

  const { data: studentsData } = useFetch([
    !!sessionStudentUid && isChannelPrivate ? `/${siteSlug}/students/` : null,
    {
      is_active: true,
      ...(channelSlug ? { channel: channelSlug } : {}),
      ...(storeSlug ? { store: storeSlug } : {}),
      ...(showcaseSlug ? { showcase: showcaseSlug } : {})
    }
  ])
  const { results = [] } = studentsData || {}
  const isValidStudent =
    !!sessionStudentUid && !!studentsData
      ? results
        ?.map((studentSiteProfile: StudentSiteProfileType) => studentSiteProfile.uid)
        .some((studentSiteProfileUid: string) => studentSiteProfileUid === sessionStudentUid)
      : undefined

  const isLoading =
    (!channelData && !channelDataError) ||
    (!siteData && !siteDataError) ||
    isAuthenticated === null ||
    (!!sessionStudentUid && isChannelPrivate && isValidStudent === undefined)

  // Render
  if (isLoading) return <LoadingOverlay loader={<Loader />} visible /> // Loading

  if (siteData?.maintenance) return <Redirect url={'/maintenance'} replace />

  if (channelData?.maintenance) return <Redirect url={`/${channelSlug}/maintenance`} replace />

  if (channelDataError) {
    return (
      <ViewError
        title="Erro"
        message="Ops... Houve um erro ao carregar esta página. Tente novamente ou entre em contato conosco."
      />
    ) // Error view if initial data and retrieve failed
  }

  if (channelData.privacy !== 'public' && isAuthenticated === false) {
    return !indexPath ? (
      <Redirect url={`/${channelSlug}`} callbackUrl={`${router.asPath}`} />
    ) : (
      <WelcomeAuth siteData={siteData} channelData={channelData} />
    )
  }

  if (!!channelProfile && !!isAuthenticated && !isAuthorized && !isActive) {
    return (
      <ViewError
        title="Conta inativa"
        message="Sua conta se encontra inativa para este canal. Caso isso não deveria acontecer, entre em contato conosco."
        icon={<IconUserOff />}
      />
    ) // Error view for inactive users
  }

  if (channelData.privacy === 'protected' && !!isAuthenticated && !isAuthorized) {
    return <ProtectedRequest channelData={channelData} permissions={permissionsData} />
  }

  if (isChannelPrivate && !!isAuthenticated && !!permissionsData && !isAuthorized) {
    return <ViewDenied />
  }

  if (
    isChannelPrivate &&
    !!isAuthenticated &&
    siteData?.beneficiary !== 'shopper' &&
    !sessionStudentUid
  ) {
    return <Redirect url="/accounts/profiles" />
  }

  if (
    isChannelPrivate &&
    !!isAuthenticated &&
    siteData?.beneficiary !== 'shopper' &&
    !!sessionStudentUid &&
    isValidStudent === false
  ) {
    return <Redirect url="/accounts/profiles" />
  }

  if (isEmailConfirmationRequired(siteData?.requireEmailConfirmation, isEmailConfirmed)) {
    return <Redirect url="/accounts/confirm-email" callbackUrl={router.asPath} replace />
  }

  return <>{children}</>
}
